/**
 * Kwota w złotych z dwoma miejscami po przecinku: „30 000,00”, „6 900,00”.
 *
 * `useGrouping: 'always'` jest konieczne. Polska reguła CLDR (minimumGroupingDigits = 2)
 * nie grupuje liczb czterocyfrowych, więc zwykłe `Intl.NumberFormat('pl-PL')` dawało
 * na karcie oferty „30 000,00 zł” i tuż pod nim „6900,00 zł”. Pismo (serwer) grupuje zawsze.
 */
const MONEY = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: 'always',
});

export const formatMoney = (n: number): string => MONEY.format(n);

/** Formatery z symbolem waluty, po jednym na kod — tabele faktur i wyciągów formatują setki kwot. */
const BY_CURRENCY = new Map<string, Intl.NumberFormat>();

/**
 * Kwota z walutą: „6 900,00 zł”, „1 250,00 €”; brak kwoty → „—”. Grupuje jak `formatMoney`.
 *
 * Kod waluty spoza trzech liter (backend sprawdza tylko długość, więc „zł.” doleci) rzuca w `Intl`
 * RangeError, który wygasiłby cały ekran — wtedy kwota i kod tekstem: „1 250,00 zł.”. Nieznany,
 * ale trzyliterowy kod („PLM”) `Intl` wypisuje sam: „1 250,00 PLM”.
 */
export function formatCurrency(n: number | null | undefined, currency = 'PLN'): string {
  if (n == null) return '—';
  let fmt = BY_CURRENCY.get(currency);
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat('pl-PL', { style: 'currency', currency, useGrouping: 'always' });
    } catch {
      return `${formatMoney(n)} ${currency}`;
    }
    BY_CURRENCY.set(currency, fmt);
  }
  return fmt.format(n);
}

const PLN_WHOLE = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  maximumFractionDigits: 0,
  useGrouping: 'always',
});

/** Pełne złote, bez groszy — sumy i kolumny list zleceń i prac: „30 000 zł”; brak kwoty → „—”. */
export const formatPlnWhole = (n: number | null | undefined): string => (n == null ? '—' : PLN_WHOLE.format(n));
